import { toast } from 'sonner';
import { useAssetGroupStore } from '@/store/asset-group.store';
import { useTenantStore } from '@/store/tenant.store';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class AssetGroupIntegration {
    constructor(service) {
        this.service = service;
    }

    /**
     * Get error message from error object or fallback
     */
    _getErrorData(error) {
        return { message: getIntegrationErrorMessage(error) };
    }

    async fetchAssetGroups(params = {}) {
        const store = useAssetGroupStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        if (!tenantId) return;

        store.setLoading(true);
        store.setError(null);

        try {
            const currentPagination = store.pagination;

            // Merge params with store state to persist filters/search/sort when only one changes
            const search = params.search !== undefined ? params.search : store.search;
            const sortBy = params.sortBy || store.sorting.sortBy;
            const sortOrder = params.sortDirection || store.sorting.sortDirection;
            const filters = params.columnFilters || store.columnFilters || {};

            // Map filters to backend format (prefix with 'filter_')
            const mappedFilters = {};
            Object.keys(filters).forEach(key => {
                mappedFilters[`filter_${key}`] = filters[key];
            });

            const queryParams = {
                page: params.page || currentPagination.currentPage,
                pageSize: params.pageSize || currentPagination.itemsPerPage,
                search,
                sortBy,
                sortOrder,
                ...mappedFilters, // Add prefixed dynamic filters
                tenantId
            };

            const response = await this.service.getAll(queryParams);

            store.setData(response.data);

            const meta = response.meta || {};
            store.setPagination({
                currentPage: parseInt(meta.page) || 1,
                itemsPerPage: parseInt(meta.pageSize) || 10,
                totalItems: parseInt(meta.total) || 0,
                totalPages: parseInt(meta.totalPages) || 1,
                hasNextPage: (parseInt(meta.page) || 1) < (parseInt(meta.totalPages) || 1),
                hasPreviousPage: (parseInt(meta.page) || 1) > 1
            });

            if (response.uniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const { message, details } = this._getErrorData(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message, details };
        } finally {
            store.setLoading(false);
        }
    }

    async getAssetGroup(id) {
        const store = useAssetGroupStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.getById(id);
            store.setSelectedItem(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const { message, details } = this._getErrorData(error);
            toast.error(message);
            return { success: false, error: message, details };
        } finally {
            store.setLoading(false);
        }
    }

    async createAssetGroup(data) {
        const store = useAssetGroupStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        try {
            const response = await this.service.create({
                ...data,
                tenantId
            });

            toast.success(i18next.t('assetInventoryAssetGroup:messages.createSuccess', {
                defaultValue: 'Asset group created',
            }));

            // Refresh list
            await this.fetchAssetGroups();
            store.closeForm();

            return { success: true, data: response.data };
        } catch (error) {
            const { message, details } = this._getErrorData(error);
            // Only toast if it's not a validation error (form will handle validation)
            if (!details) {
                toast.error(message);
            }
            return { success: false, error: message, details };
        } finally {
            store.setLoading(false);
        }
    }

    async updateAssetGroup(id, data) {
        const store = useAssetGroupStore.getState();
        store.setLoading(true);

        const targetId = id || data.id || data._id;

        try {
            const response = await this.service.update(targetId, data);

            toast.success(i18next.t('assetInventoryAssetGroup:messages.updateSuccess', {
                defaultValue: 'Asset group updated',
            }));

            // Refresh list
            await this.fetchAssetGroups();
            store.closeForm();

            return { success: true, data: response.data };
        } catch (error) {
            const { message, details } = this._getErrorData(error);
            if (!details) {
                toast.error(message);
            }
            return { success: false, error: message, details };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteAssetGroup(id) {
        const store = useAssetGroupStore.getState();
        store.setLoading(true);

        const targetId = typeof id === 'object' ? (id.id || id._id) : id;

        try {
            await this.service.delete(targetId);

            toast.success(i18next.t('assetInventoryAssetGroup:messages.deleteSuccess', {
                defaultValue: 'Asset group deleted',
            }));

            // Update local state immediately for better UX or refresh
            await this.fetchAssetGroups();

            return { success: true };
        } catch (error) {
            const { message, details } = this._getErrorData(error);
            toast.error(message);
            return { success: false, error: message, details };
        } finally {
            store.setLoading(false);
        }
    }

    async bulkDeleteAssetGroups(ids) {
        const store = useAssetGroupStore.getState();
        store.setLoading(true);

        // Ensure ids are clear strings
        const cleanIds = Array.isArray(ids) ? ids.map(id => typeof id === 'object' ? (id.id || id._id) : id) : [];

        try {
            const response = await this.service.batchDelete(cleanIds);

            const deletedCount = response.data?.deletedCount || response.deletedCount || cleanIds.length;
            toast.success(i18next.t('assetInventoryAssetGroup:messages.bulkDeleteSuccess', {
                count: deletedCount,
                defaultValue: '{{count}} asset groups deleted',
            }));

            await this.fetchAssetGroups();

            return { success: true };
        } catch (error) {
            const { message, details } = this._getErrorData(error);
            toast.error(message);
            return { success: false, error: message, details };
        } finally {
            store.setLoading(false);
        }
    }
}
